<script setup>
import { onBeforeMount, ref, defineAsyncComponent, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import opmStore from '@/lib/storeOwnerProjectManager';


const route = useRoute();
const router = useRouter();

const pageData = ref();
const actions = ref();
const tables = ref();
const documents = ref();

// Dynamically import the component based on the route parameter
const entityTypeComponent = shallowRef(null);

import { useButtonType, useButtonClick } from "@/assets/modules/composables/button.js";
const { buttonType } = useButtonType();
const { buttonClick } = useButtonClick(pageData, null);

onBeforeMount(async() => {
    entityTypeComponent.value = defineAsyncComponent(() =>
        import(`@/components/views/forms/${route.params.entity_type}Form.vue`)
    );
    let query = [];
    if(route?.query?.refetchSchemas){
        query.push({
            key: 'refetchSchemas',
            value: route.query.refetchSchemas,
        });
    }
    const response = await opmStore.dispatch("getPageData",{
        entityType: route.params.entity_type,
        entityId: route.params.entity_id,
        isMainData: true,
        query,
    });
    if(response?.success){
        pageData.value = response.data;
        opmStore.dispatch("setBreadcrumbs", response.meta.breadcrumbs);
        actions.value = response.meta.actions ||[];
        tables.value = response.meta.tables || [];
        documents.value = response.meta.documents || [];
    }
})
function tableRowClick(item, row){
    router.push(row.item.href.replace(/^\/#/, '')); // Navigate to the cleaned href
}
async function viewDocument(item, row){
    var loadPayload = {
        Key: row.item.value
    };

    await opmStore.dispatch("fetchDownload", loadPayload)
}
</script>

<template>
    <v-container fluid v-if='pageData'>
        <v-form ref="form" lazy-validation>
            <v-row>
                <v-col>
                    <h1 v-if='pageData?.name?.value'>{{ pageData.name.value }}</h1>
                    <h1 v-if='pageData?.first_name?.value'>{{ pageData.first_name.value }} {{ pageData.last_name.value }}</h1>
                </v-col>
                <v-col v-if='actions.length' class='ga-4' cols='auto'>
                    <v-btn
                        v-for="action in actions" :key="action.href"    
                        color="primary" 
                        :variant="buttonType(action.importance)" 
                        @click="buttonClick(action)"
                    >
                        {{action.title}}
                    </v-btn>
                </v-col>
            </v-row>


            <!-- Dynamically render the imported component -->
            <component 
                :is="entityTypeComponent" 
                v-if="entityTypeComponent"
                :pageData="pageData"
                >
            </component>



            <template v-if="tables.length">
                <v-row v-for="(tableObj, index) in tables" :key="index">
                    <v-col class="mt-12">
                        <!-- Access the first key of the object dynamically -->
                        <h3>{{ Object.values(tableObj)[0].title }}</h3>
                        <v-data-table
                            :headers="Object.values(tableObj)[0].tableHeaders"
                            :items="Object.values(tableObj)[0].tableData"
                            :item-key="item => item.id || index"
                            :items-per-page="100"
                            disable-sort
                            @click:row="tableRowClick"
                        >
                        </v-data-table>
                    </v-col>
                </v-row>
            </template>

            <template v-if="documents.length">
                <v-row>
                    <v-col>
                        <h3>Documents</h3>
                        <v-data-table
                            :headers="[
                                { title: 'Name', key: 'label' },
                                { title: 'Created', key: 'timestamp' },
                            ]"
                            :items="documents"
                            :item-key="documents.value"
                            :items-per-page="100"
                            disable-sort
                            @click:row="viewDocument"
                            >
                        </v-data-table>
                    </v-col>
                </v-row>
            </template>



        </v-form>
    </v-container>
</template>